import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// ==================== INTERFACES ====================

export interface Vorgang {
  id: string;
  titel: string;
  beschreibung: string | null;
  kategorie: string;
  status: string;
  prioritaet: string;
  zustaendig: string | null;
  zustaendig_liste: string[];
  business_unit: string;
  wiedervorlage_datum: string | null;
  wiedervorlage_notiz: string | null;
  frist: string | null;
  betrag: number | null;
  waehrung: string;
  fortschritt: number;
  erstellt_von: string | null;
  abgeschlossen_am: string | null;
  created_at: string;
  updated_at: string;
  // Computed counts (from joins)
  _aufgaben_count?: number;
  _dokumente_count?: number;
  _notizen_count?: number;
}

export interface VorgangVerknuepfung {
  id: string;
  vorgang_id: string;
  verknuepfung_typ: string; // 'dokument', 'aufgabe', 'kontakt', 'akte'
  verknuepfung_id: string;
  notiz: string | null;
  created_at: string;
}

export interface VorgangNotiz {
  id: string;
  vorgang_id: string;
  typ: string; // 'notiz', 'telefonat', 'email', 'status_aenderung', 'system'
  inhalt: string;
  erstellt_von: string | null;
  created_at: string;
}

export interface NeuerVorgang {
  titel: string;
  beschreibung?: string;
  kategorie?: string;
  prioritaet?: string;
  zustaendig?: string;
  business_unit?: string;
  wiedervorlage_datum?: string;
  frist?: string;
  betrag?: number;
}

// ==================== KONSTANTEN ====================

export const VORGANG_STATUS = [
  { value: 'offen', label: 'Offen', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'warten', label: 'Warten auf Dritte', color: 'bg-orange-100 text-orange-800' },
  { value: 'abgeschlossen', label: 'Abgeschlossen', color: 'bg-green-100 text-green-800' },
  { value: 'archiviert', label: 'Archiviert', color: 'bg-gray-100 text-gray-600' },
] as const;

export const VORGANG_KATEGORIEN = [
  { value: 'forderung', label: 'Forderungsmanagement', icon: '💰' },
  { value: 'steuer', label: 'Steuer & Finanzen', icon: '📊' },
  { value: 'recht', label: 'Rechtlicher Konflikt', icon: '⚖️' },
  { value: 'projekt', label: 'Internes Projekt', icon: '📋' },
  { value: 'personal', label: 'Personal', icon: '👤' },
  { value: 'immobilie', label: 'Immobilien', icon: '🏠' },
  { value: 'versicherung', label: 'Versicherung', icon: '🛡️' },
  { value: 'sonstiges', label: 'Sonstiges', icon: '📁' },
] as const;

export const VORGANG_PRIORITAETEN = [
  { value: 'niedrig', label: 'Niedrig', color: 'text-gray-500' },
  { value: 'mittel', label: 'Mittel', color: 'text-blue-500' },
  { value: 'hoch', label: 'Hoch', color: 'text-orange-500' },
  { value: 'kritisch', label: 'Kritisch', color: 'text-red-600' },
] as const;

export const NOTIZ_TYPEN = [
  { value: 'notiz', label: 'Notiz' },
  { value: 'telefonat', label: 'Telefonat' },
  { value: 'email', label: 'E-Mail' },
  { value: 'meeting', label: 'Besprechung' },
  { value: 'status_aenderung', label: 'Statusänderung' },
  { value: 'system', label: 'System' },
] as const;

// ==================== HOOKS ====================

/**
 * Hook für CRUD-Operationen auf Vorgänge
 */
export function useVorgaenge() {
  const [vorgaenge, setVorgaenge] = useState<Vorgang[]>([]);
  const [loading, setLoading] = useState(true);

  const loadVorgaenge = useCallback(async (filter?: { status?: string; kategorie?: string }) => {
    setLoading(true);
    try {
      let query = supabase
        .from('vorgaenge')
        .select('*')
        .order('updated_at', { ascending: false });

      if (filter?.status && filter.status !== 'alle') {
        query = query.eq('status', filter.status);
      }
      if (filter?.kategorie && filter.kategorie !== 'alle') {
        query = query.eq('kategorie', filter.kategorie);
      }

      const { data, error } = await query;
      if (error) throw error;
      setVorgaenge(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Vorgänge:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createVorgang = useCallback(async (vorgang: NeuerVorgang): Promise<Vorgang | null> => {
    try {
      const { data, error } = await supabase
        .from('vorgaenge')
        .insert({
          titel: vorgang.titel,
          beschreibung: vorgang.beschreibung || null,
          kategorie: vorgang.kategorie || 'sonstiges',
          prioritaet: vorgang.prioritaet || 'mittel',
          zustaendig: vorgang.zustaendig || null,
          business_unit: vorgang.business_unit || 'gutachter_koop',
          wiedervorlage_datum: vorgang.wiedervorlage_datum || null,
          frist: vorgang.frist || null,
          betrag: vorgang.betrag || null,
          status: 'offen',
          fortschritt: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Fehler beim Erstellen des Vorgangs:', err);
      return null;
    }
  }, []);

  const updateVorgang = useCallback(async (id: string, updates: Partial<Vorgang>) => {
    try {
      const { error } = await supabase
        .from('vorgaenge')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      setVorgaenge(prev => prev.map(v => v.id === id ? { ...v, ...updates } : v));
      return true;
    } catch (err) {
      console.error('Fehler beim Aktualisieren:', err);
      return false;
    }
  }, []);

  const deleteVorgang = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('vorgaenge')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVorgaenge(prev => prev.filter(v => v.id !== id));
      return true;
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      return false;
    }
  }, []);

  return { vorgaenge, loading, loadVorgaenge, createVorgang, updateVorgang, deleteVorgang };
}

/**
 * Hook für Verknüpfungen eines Vorgangs
 */
export function useVorgangVerknuepfungen(vorgangId: string | null) {
  const [verknuepfungen, setVerknuepfungen] = useState<VorgangVerknuepfung[]>([]);
  const [loading, setLoading] = useState(false);

  const loadVerknuepfungen = useCallback(async () => {
    if (!vorgangId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vorgang_verknuepfungen')
        .select('*')
        .eq('vorgang_id', vorgangId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVerknuepfungen(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Verknüpfungen:', err);
    } finally {
      setLoading(false);
    }
  }, [vorgangId]);

  useEffect(() => { loadVerknuepfungen(); }, [loadVerknuepfungen]);

  const addVerknuepfung = useCallback(async (typ: string, id: string, notiz?: string) => {
    if (!vorgangId) return false;
    try {
      const { error } = await supabase
        .from('vorgang_verknuepfungen')
        .insert({
          vorgang_id: vorgangId,
          verknuepfung_typ: typ,
          verknuepfung_id: id,
          notiz: notiz || null,
        });

      if (error) throw error;
      await loadVerknuepfungen();
      return true;
    } catch (err) {
      console.error('Fehler beim Hinzufügen der Verknüpfung:', err);
      return false;
    }
  }, [vorgangId, loadVerknuepfungen]);

  const removeVerknuepfung = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('vorgang_verknuepfungen')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVerknuepfungen(prev => prev.filter(v => v.id !== id));
      return true;
    } catch (err) {
      console.error('Fehler beim Entfernen der Verknüpfung:', err);
      return false;
    }
  }, []);

  return { verknuepfungen, loading, addVerknuepfung, removeVerknuepfung, refetch: loadVerknuepfungen };
}

/**
 * Hook für Notizen/Aktivitätslog eines Vorgangs
 */
export function useVorgangNotizen(vorgangId: string | null) {
  const [notizen, setNotizen] = useState<VorgangNotiz[]>([]);
  const [loading, setLoading] = useState(false);

  const loadNotizen = useCallback(async () => {
    if (!vorgangId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vorgang_notizen')
        .select('*')
        .eq('vorgang_id', vorgangId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotizen(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Notizen:', err);
    } finally {
      setLoading(false);
    }
  }, [vorgangId]);

  useEffect(() => { loadNotizen(); }, [loadNotizen]);

  const addNotiz = useCallback(async (inhalt: string, typ: string = 'notiz', erstellt_von?: string) => {
    if (!vorgangId) return false;
    try {
      const { error } = await supabase
        .from('vorgang_notizen')
        .insert({
          vorgang_id: vorgangId,
          typ,
          inhalt,
          erstellt_von: erstellt_von || null,
        });

      if (error) throw error;
      await loadNotizen();
      return true;
    } catch (err) {
      console.error('Fehler beim Hinzufügen der Notiz:', err);
      return false;
    }
  }, [vorgangId, loadNotizen]);

  const deleteNotiz = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('vorgang_notizen')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setNotizen(prev => prev.filter(n => n.id !== id));
      return true;
    } catch (err) {
      console.error('Fehler beim Löschen der Notiz:', err);
      return false;
    }
  }, []);

  return { notizen, loading, addNotiz, deleteNotiz, refetch: loadNotizen };
}

// ==================== HELPER ====================

export function getStatusInfo(status: string) {
  return VORGANG_STATUS.find(s => s.value === status) || VORGANG_STATUS[0];
}

export function getKategorieInfo(kategorie: string) {
  return VORGANG_KATEGORIEN.find(k => k.value === kategorie) || VORGANG_KATEGORIEN[VORGANG_KATEGORIEN.length - 1];
}

export function getPrioritaetInfo(prioritaet: string) {
  return VORGANG_PRIORITAETEN.find(p => p.value === prioritaet) || VORGANG_PRIORITAETEN[1];
}
